import { Injectable, inject } from "@angular/core";
import { Observable, merge, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { VirkeskopKontraktstatus } from "~app/common/models/constants";
import { Kontrakt } from "../models/kontrakt-models";
import { Planeringsstatus } from "../models/planeringsstatus";
import { KontaktpersonMediatorService } from "./kontaktperson-mediator.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

@Injectable()
export class KontraktStatusService {
    readonly kontraktStatusStates$: Observable<string[]>;

    private readonly kontaktpersonMediatorService = inject(KontaktpersonMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);

    constructor() {
        this.kontraktStatusStates$ = merge(
            this.kontraktMediatorService.atgardUpdate$,
            this.kontraktMediatorService.kontrakt$,
            this.kontraktMediatorService.kontraktUpdate$
        ).pipe(
            switchMap(() => this.kontraktMediatorService.kontrakt$),
            switchMap(kontrakt => this.getStates(kontrakt))
        );
    }

    private getAnbudStates(): Observable<string[]> {
        return this.kontaktpersonMediatorService.hasKontakt$.pipe(
            map(hasKontakt => {
                const states = [
                    VirkeskopKontraktstatus.Anbud,
                    VirkeskopKontraktstatus.EjKopt,
                    VirkeskopKontraktstatus.Vilande
                ];

                if (hasKontakt) {
                    states.splice(1, 0, VirkeskopKontraktstatus.Kontrakt);
                }
                return states;
            })
        );
    }

    private getEjKoptStates(): Observable<string[]> {
        return of([
            VirkeskopKontraktstatus.Anbud,
            VirkeskopKontraktstatus.EjKopt
        ]);
    }

    private getKontraktStates(): Observable<string[]> {
        return this.kontraktMediatorService.atgarder$.pipe(
            map(atgarder => {
                if (atgarder.some(atg => Planeringsstatus.IsTillangligEllerSenare(atg.status) && !atg.isLeveransvirke)) {
                    return [VirkeskopKontraktstatus.Kontrakt];
                }
                return [
                    VirkeskopKontraktstatus.Kontrakt,
                    VirkeskopKontraktstatus.Anbud
                ];
            })
        );
    }

    private getStates(kontrakt: Kontrakt): Observable<string[]> {
        switch (kontrakt.status) {
            case VirkeskopKontraktstatus.EjKopt:
                return this.getEjKoptStates();
            case VirkeskopKontraktstatus.Vilande:
                return this.getVilandeStates();
            case VirkeskopKontraktstatus.Anbud:
                return this.getAnbudStates();
            case VirkeskopKontraktstatus.Kontrakt:
                return this.getKontraktStates();
            case VirkeskopKontraktstatus.Makulerat:
                return of([VirkeskopKontraktstatus.Makulerat]);
            default:
                return of([]);
        }
    }

    private getVilandeStates(): Observable<string[]> {
        return of([
            VirkeskopKontraktstatus.Anbud,
            VirkeskopKontraktstatus.EjKopt,
            VirkeskopKontraktstatus.Vilande
        ]);
    }
}
